import { gql, NetworkStatus, useQuery } from '@apollo/client'
import cx from 'classnames'
import PageComponent from 'components/Dashboard/PageComponent'
import { addMinutes, getHours, getMinutes, setHours, setMinutes } from 'date-fns'
import { range } from 'lodash'
import { FaSync } from 'react-icons/fa'
import { DashboardTitle } from '../DashboardTitleContext'
import {
  BookedAppointmentsQuery,
  BookedAppointmentsQuery_bookedAppointments_appointments,
} from './__generated__/BookedAppointmentsQuery'

const bookedAppointmentsQuery = gql`
  query BookedAppointmentsQuery {
    bookedAppointments {
      id
      date
      appointments {
        id
        availableQty
        bookedQty
        orders
      }
    }
  }
`

const getStyle = (appt: BookedAppointmentsQuery_bookedAppointments_appointments) => {
  if (appt.bookedQty === 0) return 'has-text-grey-light'

  if (appt.bookedQty < 2) return 'has-background-success-light'

  if (appt.bookedQty === 2) return 'has-background-warning-light'

  return 'has-background-danger-light'
}

const VideoAppointments = () => {
  const { error, data, networkStatus, refetch } = useQuery<BookedAppointmentsQuery>(bookedAppointmentsQuery, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  if (error) {
    throw error
  }

  const start = setMinutes(setHours(new Date(), 8), 0)

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">{networkStatus === NetworkStatus.loading ? 'Loading...' : 'Bookings'}</p>
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus !== NetworkStatus.ready,
              })}
              disabled={networkStatus !== NetworkStatus.ready}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </nav>

      <div className="table-container" style={{ overflowY: 'auto', maxHeight: '80vh' }}>
        <table className="table is-bordered is-hoverable is-fullwidth has-sticky-header has-sticky-column has-sticky-footer">
          <thead>
            <tr>
              <th>Date</th>
              {range(72).map((i) => {
                const sessionDate = addMinutes(start, 10 * i)

                return (
                  <th key={i}>
                    {getHours(sessionDate).toString().padStart(2, '0')}:
                    {getMinutes(sessionDate).toString().padStart(2, '0')}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {data?.bookedAppointments.map((date) => (
              <tr key={date.date} style={{ whiteSpace: 'nowrap' }}>
                <th>{date.date}</th>

                {date.appointments.map((appt) => (
                  <td key={appt.id} className={getStyle(appt)}>
                    {appt.bookedQty}/{appt.availableQty}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Date</th>
              {range(72).map((i) => {
                const sessionDate = addMinutes(start, 10 * i)

                return (
                  <th key={i}>
                    {getHours(sessionDate).toString().padStart(2, '0')}:
                    {getMinutes(sessionDate).toString().padStart(2, '0')}
                  </th>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  )
}

const VideoAppointmentsPage: PageComponent = () => {
  return (
    <div className="container">
      <DashboardTitle title="Video Appointments" />

      <VideoAppointments />
    </div>
  )
}

VideoAppointmentsPage.displayName = 'Video Appointments'
VideoAppointmentsPage.path = '/video-appointments'

export default VideoAppointmentsPage
